import { useMutation, useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

/**
 * Data hooks for Code RAG — a reader over the shared `cinch_code` Qdrant index
 * that the nightly sync job on Spark maintains.
 *
 * Overview is a normal cached query. Search and Ask are mutations rather than
 * queries on purpose: both are user-initiated, both cost an embedding call (and
 * Ask an LLM turn), so neither should re-run on window focus or remount.
 */

export interface RepoFreshness {
  repo: string;
  chunks: number;
  /** Newest commit date actually present in the index for this repo. */
  indexed_through: string | null;
  last_author: string | null;
  last_commit_message: string | null;
  last_file: string | null;
  age_days: number | null;
}

export interface SyncStatus {
  last_run: string | null;
  repos_checked: number | null;
  files_changed: number | null;
  age_hours: number | null;
  available: boolean;
}

export interface IndexHealth {
  status: string;
  points: number;
  indexed_vectors: number;
  segments: number;
  vector_size: number;
  distance: string;
  embed_model: string;
  collection: string;
  /** Branch every repo is indexed from — `main` for all nine. */
  branch: string;
  indexed_fields: string[];
}

export interface CorpusSize {
  chars: number;
  lines: number;
  chunks: number;
  est_tokens: number;
  chars_per_token: number;
  /** How many 1M-token context windows the corpus would fill. */
  context_windows_1m: number;
  cached_at: string | null;
}

export interface FacetRow {
  value: string;
  count: number;
}

export interface OverviewResponse {
  health: IndexHealth;
  repos: RepoFreshness[];
  languages: FacetRow[];
  chunk_types: FacetRow[];
  sync: SyncStatus;
}

export interface SearchHit {
  score: number;
  repo: string;
  file_path: string;
  line_start: number | null;
  line_end: number | null;
  function_name: string | null;
  language: string | null;
  chunk_type: string | null;
  content: string | null;
  last_author: string | null;
  last_modified: string | null;
  last_commit_message: string | null;
  commit_hash: string | null;
}

export interface SearchResponse {
  hits: SearchHit[];
  took_ms: number;
}

export interface SearchBody {
  query: string;
  repo?: string | null;
  language?: string | null;
  limit?: number;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatResponse {
  answer: string;
  model: string;
  citations: SearchHit[];
}

export interface AiModel {
  id: string;
  provider: string;
  name: string;
  is_default?: boolean;
}

export interface AdminConfig {
  collection: string;
  qdrant_url: string;
  embed_model: string;
  payload_schema: Record<string, unknown>;
  missing_indexes: string[];
  console_url: string;
}

export function useRagOverview() {
  return useQuery({
    queryKey: ["code_rag", "overview"],
    queryFn: () => apiFetch<OverviewResponse>("/code-rag/overview"),
    // The index only changes when the nightly sync runs, so don't re-fetch on
    // every focus — but do go stale within a working session so a manual
    // re-index shows up without a hard reload.
    staleTime: 5 * 60_000,
  });
}

/**
 * Corpus size — deliberately its own query rather than part of the overview.
 *
 * Computing it scans every chunk's content: ~1.6s on the Spark host itself but
 * ~9s from the backend over the LAN, which is far too slow to hold up the whole
 * Overview tab. Server-side it is cached for 12 hours (the corpus only moves
 * when the nightly sync runs), so this is a one-off cost per deploy-day and
 * 6ms thereafter. The tile renders its own loading state meanwhile.
 */
export function useRagCorpus() {
  return useQuery({
    queryKey: ["code_rag", "corpus"],
    queryFn: () => apiFetch<CorpusSize>("/code-rag/corpus"),
    // Already cached server-side; this just avoids re-asking within a session.
    staleTime: 30 * 60_000,
  });
}

/**
 * Search is a QUERY, not a mutation, even though it POSTs.
 *
 * The search is fully derived from URL state (?q, ?repo, ?lang) and is
 * idempotent, so modelling it as a query is both more accurate and better
 * behaved: identical searches are cached, back/forward navigation restores
 * results instead of re-running, and there is no mount effect to fire.
 *
 * That last point is not cosmetic. Driving a mutation from a mount effect
 * breaks under React StrictMode — the simulated unmount/remount detaches the
 * mutation observer, the response lands on a dead observer, and `isPending`
 * stays true forever. A query keyed on the params has no such failure mode.
 */
export function useRagSearch(params: SearchBody) {
  const query = params.query.trim();
  return useQuery({
    queryKey: ["code_rag", "search", query, params.repo ?? "", params.language ?? "", params.limit ?? 15],
    queryFn: () =>
      apiFetch<SearchResponse>("/code-rag/search", {
        method: "POST",
        body: { ...params, query },
      }),
    enabled: query.length > 0,
    // An embedding + k-NN round trip per keystroke-less submit is cheap, but
    // re-running it on every window focus is pure waste: the index only moves
    // when the nightly sync does.
    staleTime: 5 * 60_000,
  });
}

export function useRagChat() {
  return useMutation({
    mutationFn: (body: {
      messages: ChatMessage[];
      repo?: string | null;
      language?: string | null;
      model_id?: string | null;
      context_chunks?: number;
    }) => apiFetch<ChatResponse>("/code-rag/chat", { method: "POST", body }),
  });
}

export function useRagModels() {
  return useQuery({
    queryKey: ["code_rag", "models"],
    queryFn: () =>
      apiFetch<{ models: AiModel[]; default_id: string | null }>("/code-rag/models"),
    staleTime: Infinity, // the model roster changes only when Ollama does
  });
}

export function useRagAdminConfig(enabled: boolean) {
  return useQuery({
    queryKey: ["code_rag", "admin", "config"],
    queryFn: () => apiFetch<AdminConfig>("/code-rag/admin/config"),
    enabled,
  });
}

export function useCreatePayloadIndex() {
  return useMutation({
    mutationFn: (field: string) =>
      apiFetch<unknown>(`/code-rag/admin/index/${field}`, { method: "POST" }),
  });
}
